// Global Instructions Rule Applied!
//
// The "?" affordance that opens a screen's user guide.
//
// Self-contained: it owns its own drawer state, so a screen adopts help by
// dropping this in with an articleKey — the same shape as AggregationsButton,
// the closest existing precedent for a toolbar button that owns a drawer.
//
// Four variants, one per surface the app actually has:
//   ribbon  — inside the blue ViewToolbar ribbon (white-on-blue toolbar class)
//   title   — beside a page/tab title (the Connected Agents placement)
//   section — inside a FormSection headerActions slot
//   link    — an inline "Read the guide" text link within page content
//
// Reservoir had no in-app help at all before this, so the button and the
// non-modal drawer are recorded as an approved pattern in
// docs/FRONTEND_DESIGN_PATTERNS.md.

import React, { useState } from 'react'
import { Button as AntButton, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons'
import { HelpDrawer } from './HelpDrawer.tsx'

export type HelpButtonVariant = 'ribbon' | 'title' | 'section' | 'link'

export interface HelpButtonProps {
  /** cfg.help_articles.article_key, e.g. 'agent_access.my_agents'. */
  articleKey?: string
  variant?: HelpButtonVariant
  /** Optional visible label. Icon-only when omitted (except `link`). */
  label?: string
  tooltip?: string
}

// The shared non-CTA toolbar button treatment, matching
// ViewToolbar/parts/ExportToolbarButton.tsx and ViewConfig.tsx.
const RIBBON_CLASS =
  '!border-0 !shadow-none !bg-transparent !text-white hover:!bg-white/10 hover:!text-white [&_.ant-btn-icon]:!text-white'

export function HelpButton({
  articleKey,
  variant = 'section',
  label,
  tooltip = 'User guide'
}: HelpButtonProps) {
  const [open, setOpen] = useState(false)

  // A screen that has no guide yet simply shows no "?" — never a dead control.
  if (!articleKey) return null

  const icon = <FontAwesomeIcon icon={faCircleQuestion} />

  const button =
    variant === 'link' ? (
      <AntButton
        type='link'
        size='small'
        icon={icon}
        onClick={() => setOpen(true)}
        aria-label={tooltip}
        className='!px-0'
      >
        {label || 'Read the guide'}
      </AntButton>
    ) : (
      <AntButton
        type='text'
        size='small'
        icon={icon}
        onClick={() => setOpen(true)}
        aria-label={label ? undefined : tooltip}
        className={variant === 'ribbon' ? RIBBON_CLASS : undefined}
      >
        {label}
      </AntButton>
    )

  return (
    <>
      <Tooltip title={tooltip} placement='bottom'>
        {button}
      </Tooltip>
      <HelpDrawer articleKey={articleKey} open={open} onClose={() => setOpen(false)} />
    </>
  )
}
